/* global localStorage */
import moment from 'moment'
import React, { CSSProperties } from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router-dom'
import _ from 'underscore'
import { TidewaysContext } from './app/context'
import { Entity } from './app/entity'
import Navigator from './app/navigator'
import { ExtendedProfileCriteria } from './domain/ProfileCriteria'
import { TraceList } from './domain/TraceSummary'
import Pager from './errors/list/pager'
import DropdownSelector from './filter/dropdownSelector'
import OnlineDropdownSelector from './filter/onlineDropdownSelector'
import RangeSelector from './filter/rangeSelector'
import Selector from './filter/selector'
import TextInput from './filter/TextInput'
import LegacyNavigatorWrapper from './helper/legacyNavigatorWrapper'
import Link from './helper/link'
import Loading from './helper/loading'
import { parseQueryString } from './helper/queryString'
import ProfileTable from './monitor/profile_table'
import { ApplicationState } from './redux/ApplicationState'
import TraceNavigation from './trace-navigation'

interface OwnProps {
    listRoute: string
    fixedCriteria?: { [key: string]: boolean }
    profileRoute?: string
    params: { profile?: string | string[] }
    // TODO: Replace with ContextAware
    mainFilters?: string[]
    additionalFilters?: { [filterName: string]: { queryKeys: string[]; label: string } }
    actions?: string[]
    showTabs: boolean
    error?: { advancedParseError: string }
    hasArchivedTraces?: boolean
}

interface DispatchProps {
    onCriteriaModified: (values: any) => void
}

interface FilterDate {
    minDate: moment.Moment
    maxDate: moment.Moment
    label: string
}

interface StateProps {
    tidewaysContext: TidewaysContext
    navigator: Navigator
    parameters: { [key: string]: string }
    loading: boolean
    data: TraceList | null
    criteria: ExtendedProfileCriteria | null
}

type Props = OwnProps & DispatchProps & StateProps & RouteComponentProps

interface State {
    additionalFilters: { [filterName: string]: { queryKeys: string[]; label: string } }
    mainFilters: string[]
    enabledFilters: string[]
    searchValue: string
    preventSearchQueryParameterOverride: boolean
}

class ProfileList extends React.Component<Props, State> {
    public static defaultProps = {
        hasArchivedTraces: false,
        fixedCriteria: {},
        listRoute: 'XhprofProfilerBundle.Profile.list',
        profileRoute: 'XhprofProfilerBundle.Profile.quick',
        mainFilters: ['transactions', 'search', 'date', 'type'],
        additionalFilters: {
            bottleneckType: { queryKeys: ['bottleneckType'], label: 'Contains Bottlenecks', inLayers: false },
            response: { queryKeys: ['minWallTime', 'maxWallTime'], label: 'Response Times' },
            dateRange: { queryKeys: ['minDate', 'maxDate'], label: 'DateRange' },
            server: { queryKeys: ['serverIds'], label: 'Server' },
            release: { queryKeys: ['releaseIds'], label: 'During Release' },
            transactionTag: { queryKeys: ['transactionTag'], label: 'Tags' },
            method: { queryKeys: ['method'], label: 'HTTP: Method' },
            host: { queryKeys: ['host'], label: 'HTTP: Host' },
            sqlCt: { queryKeys: ['layers[db][minCt]', 'layers[db][maxCt]'], label: 'SQL: Number of Queries' },
            sqlWt: {
                queryKeys: ['layers[db][minWt]', 'layers[db][maxWt]'],
                label: 'SQL: Total Duration of Queries',
            },
            httpCt: {
                queryKeys: ['layers[http][minCt]', 'layers[http][maxCt]'],
                label: 'HTTP: Number of Outgoing Requests',
            },
            httpWt: {
                queryKeys: ['layers[http][minWt]', 'layers[http][maxWt]'],
                label: 'HTTP: Total Duration of Outgoing Requests',
            },
            httpStatus: {
                queryKeys: ['layers[http][status]', 'layers[http][status]'],
                label: 'HTTP: Status',
            },
        },
        actions: ['sort', 'clear'],
        showTabs: true,
    }

    public static getDerivedStateFromProps(nextProps: Props, prevState: State) {
        // props.criteria is updated asynchronously when the route changes. This code updates the searchValue state
        // for the search field initially as soon as the URL has been parsed.
        if (
            !prevState.preventSearchQueryParameterOverride &&
            !prevState.searchValue &&
            prevState.searchValue !== nextProps.criteria?.search
        ) {
            return {
                searchValue: nextProps.criteria?.search ?? '',
            }
        }

        return null
    }

    public state: State

    private delayedSearchTimeout: NodeJS.Timeout | number | undefined | null = null

    constructor(props) {
        super(props)

        this.state = {
            additionalFilters: {},
            mainFilters: [],
            enabledFilters: [],
            searchValue: '',
            preventSearchQueryParameterOverride: false,
        }
    }

    public componentDidMount() {
        let mainFilters = this.props.mainFilters?.slice() || []

        if (this.props.fixedCriteria?.date) {
            mainFilters = mainFilters.filter(filter => filter !== 'date')
        }

        if (this.props.fixedCriteria?.opId) {
            mainFilters = mainFilters.filter(filter => filter !== 'transactions')
        }

        const additionalFilters = { ...this.props.additionalFilters }

        if (this.displayCustomAnnotations() && this.displayCustomSpans()) {
            this.props.tidewaysContext.customAnnotations.map(annotation => {
                additionalFilters['customAnnotations::' + annotation] = {
                    queryKeys: ['customAnnotations[' + annotation + ']'],
                    label: 'Annotation: ' + annotation,
                }
            })
        }

        if (!this.props.tidewaysContext.application?.license.traceHttpHostFilter) {
            delete additionalFilters.host
        }

        if (!('minDate' in this.props.parameters && 'maxDate' in this.props.parameters)) {
            delete additionalFilters.dateRange
        }
        // Enable additional filters which have a value set in the url
        // find all filters that have any of their query parameters active
        const enabledFilters = Object.keys(additionalFilters).filter(
            filterName => additionalFilters[filterName].queryKeys.filter(queryKey => queryKey in this.props.parameters).length > 0,
        )

        const searchValue = this.props.criteria && this.props.criteria.search ? this.props.criteria.search : ''

        this.setState({
            additionalFilters,
            mainFilters,
            enabledFilters,
            searchValue,
            preventSearchQueryParameterOverride: false,
        })
    }

    public setService = name => {
        this.props.tidewaysContext.setService(name)
        this.setQueryParameter({ s: name })
    }

    public setEnvironment = name => {
        this.props.tidewaysContext.setEnvironment(name)
        this.setQueryParameter({ env: name })
    }

    public setQueryParameter = values => {
        this.onCriteriaModified(values)
        this.props.navigator.replace({ ...this.props.parameters, ...values })
    }

    // Layer query params look something like 'layers[http][minCt]', but replacing 'layers[http]' fully cannot be done at
    // this point, since there are multiple filters for 'layers[http]' (i.e. count and walltime). Therefore, we
    // explicitly overwrite the relevant values.
    public setLayersQueryParameter = (values: { [key: string]: string }, layer: string) => {
        const params = {}
        Object.entries(values).forEach(([key, value]) => {
            params[`layers[${layer}][${key}]`] = value
        })

        this.setQueryParameter(params)
    }

    public getNavigator = () => {
        return new LegacyNavigatorWrapper(this.props.navigator)
    }

    public render() {
        const errorBoxStyle = {
            width: '100%',
            height: '100%',
            position: 'relative',
            minHeight: '32px',
            backgroundColor: 'fade(#f56954, 30%)',
            color: '#f56954',
        }

        const router = this.props.tidewaysContext.router
        const { tidewaysContext } = this.props
        const { bottlenecks } = tidewaysContext
        const actions = this.props.actions ?? []
        const criteria = this.props.criteria
        const { source = 'monitoring' } = this.props.parameters
        const historyView = source === 'history'

        if (!criteria || Object.keys(criteria).length === 0) {
            return null
        }

        const hours = {
            0: 'All',
            1: 'Last 60 Minutes',
            3: 'Last 3 Hours',
            6: 'Last 6 Hours',
            12: 'Last 12 Hours',
            24: 'Last Day',
            96: 'Last 3 Days',
            168: 'Last 7 Days',
            336: 'Last 14 Days',
            744: 'Last 30 Days',
        }
        const parseTypes = {
            0: 'All Traces',
            1: 'Callgraph Traces',
        }
        const sortOptions = {
            time: 'Date',
            wt: 'Response Time',
            mem: 'Memory Usage',
        }

        const days: FilterDate[] = []
        for (let i = 0; i < tidewaysContext.application.retentionDays; i++) {
            days[i] = {
                minDate: moment()
                    .utc()
                    .subtract(i, 'days')
                    .hour(0)
                    .minute(0),
                maxDate: moment()
                    .utc()
                    .subtract(i, 'days')
                    .hour(23)
                    .minute(59),
                label: moment()
                    .utc()
                    .subtract(i, 'days')
                    .format('DD. MMM YYYY'),
            }
        }

        const definitions = {
            more: (
                <DropdownSelector
                    minWidth={350}
                    type="filter"
                    head="Add Filter"
                    // @ts-ignore Underscore is typed to expect a "length" property on list parameters. This is not relevant at runtime here.
                    items={_.values(_.without(this.state.additionalFilters, 'dateRange')).map(v => v.label)}
                    selected={this.state.enabledFilters.map(k => this.state.additionalFilters[k].label) || []}
                    onUpdate={labels => {
                        const items = Object.keys(this.state.additionalFilters).filter(
                            k => labels.indexOf(this.state.additionalFilters[k].label) >= 0,
                        )
                        this.setState({ enabledFilters: items })
                        const toUnsetFilters = [this.state.enabledFilters, items].reduce((firstElement, secondElement) => {
                            return firstElement.filter(sharedItem => !secondElement.includes(sharedItem))
                        })

                        for (const i of toUnsetFilters) {
                            this.hideFilter(toUnsetFilters[i])
                        }
                    }}
                    sortCompareFunction={(a, b) => {
                        const aIsAnnotation = a.includes('Annotation: ')
                        const bIsAnnotation = b.includes('Annotation: ')

                        if (aIsAnnotation && bIsAnnotation) {
                            return a < b ? -1 : 1
                        }

                        if (aIsAnnotation) {
                            return 1
                        }

                        if (bIsAnnotation) {
                            return -1
                        }

                        return a < b ? -1 : 1
                    }}
                />
            ),
            transactions: (
                <OnlineDropdownSelector
                    type="transaction"
                    queryKey="opId"
                    selected={criteria.opId || null}
                    searchUrl={router.path('XhprofProfilerBundle.Transaction.dropdownSearchSelect', {
                        s: this.props.tidewaysContext.service,
                        env: this.props.tidewaysContext.environment,
                    })}
                    navigator={this.getNavigator()}
                    onCriteriaModified={this.onCriteriaModified}
                    openRight
                />
            ),
            date: (
                <Selector type="date" onRemove={() => this.hideFilter('date')} headText={hours[criteria.hours || 0]}>
                    <ul className="dropdown-menu" role="menu">
                        {Object.entries(hours).map(([hour, title]) => {
                            if (Number(hour) > tidewaysContext.application.retentionDays * 24) {
                                return null
                            }

                            return (
                                <li key={hour}>
                                    <a
                                        onClick={() => {
                                            this.setQueryParameter({ hours: hour, minDate: null, maxDate: null })
                                        }}
                                    >
                                        {title}
                                    </a>
                                </li>
                            )
                        })}
                        <li role="separator" className="divider"></li>
                        {days.map((day, idx) => {
                            return (
                                <li key={idx}>
                                    <a
                                        onClick={() => {
                                            this.setQueryParameter({
                                                hours: null,
                                                minDate: day.minDate.format('YYYY-MM-DD HH:mm'),
                                                maxDate: day.maxDate.format('YYYY-MM-DD HH:mm'),
                                            })
                                        }}
                                    >
                                        {day.label}
                                    </a>
                                </li>
                            )
                        })}
                    </ul>
                </Selector>
            ),
            dateRange: (
                <Selector
                    type="dateRange"
                    removable
                    onRemove={e => this.hideFilter('dateRange')}
                    disabled
                    hasCaret={false}
                    headText={criteria.minDate && criteria.maxDate ? criteria.minDate + ' - ' + criteria.maxDate : ''}
                />
            ),
            type: (
                <div className="form-group">
                    <div className="btn-group">
                        {Object.entries(parseTypes).map(([parseType, title]) => {
                            return (
                                <button
                                    key={parseType}
                                    className={
                                        parseTypes[criteria.parseType || '0'] === title ? 'btn btn-default active' : 'btn btn-default'
                                    }
                                    type="button"
                                    onClick={() => {
                                        this.setQueryParameter({ parseType })
                                    }}
                                >
                                    {title}
                                </button>
                            )
                        })}
                    </div>
                </div>
            ),
            server: (
                <OnlineDropdownSelector
                    type="server"
                    queryKey="serverIds"
                    selected={criteria.serverIds || null}
                    searchUrl={router.path('XhprofProfilerBundle.Server.dropdown')}
                    navigator={this.getNavigator()}
                    removable
                    onRemove={e => this.hideFilter('server')}
                    onCriteriaModified={this.onCriteriaModified}
                />
            ),
            customAnnotations: (customAnnotation: string) => (
                <TextInput
                    name={'customAnnotations[' + customAnnotation + ']'}
                    label={'Annotation: ' + customAnnotation}
                    initialValue={criteria.customAnnotations ? criteria.customAnnotations[customAnnotation] || '' : ''}
                    removable
                    onRemove={() => {
                        this.hideFilter('customAnnotations::' + customAnnotation)
                    }}
                    onChange={value => {
                        this.setQueryParameter({ ['customAnnotations[' + customAnnotation + ']']: value })
                    }}
                />
            ),
            response: (
                <RangeSelector
                    removable
                    onRemove={e => this.hideFilter('response')}
                    type="response"
                    min={
                        'minWallTime' in criteria && typeof criteria.minWallTime !== 'undefined' ? parseInt(criteria.minWallTime, 10) : null
                    }
                    max={
                        'maxWallTime' in criteria && typeof criteria.maxWallTime !== 'undefined' ? parseInt(criteria.maxWallTime, 10) : null
                    }
                    minKey="minWallTime"
                    maxKey="maxWallTime"
                    onClick={this.setQueryParameter}
                    ranges={[
                        { min: 0, max: 50 },
                        { min: 50, max: 250 },
                        { min: 250, max: 500 },
                        {
                            min: 500,
                            max: 2000,
                        },
                        { min: 2000, max: 4000 },
                        { min: 4000 },
                    ]}
                    unit="ms"
                    showCustom
                />
            ),
            release: (
                <OnlineDropdownSelector
                    type="release"
                    queryKey="releaseIds"
                    selected={criteria.releaseIds || null}
                    searchUrl={router.path('XhprofProfilerBundle.Event.dropdown', { type: 'release' })}
                    navigator={this.getNavigator()}
                    removable
                    onRemove={e => this.hideFilter('release')}
                    onCriteriaModified={this.onCriteriaModified}
                />
            ),
            bottleneckType: (
                <OnlineDropdownSelector
                    type="Bottleneck"
                    queryKey="bottleneckType"
                    selected={criteria.bottleneckType || null}
                    searchUrl={router.path('XhprofProfilerBundle.Profile.dropdownSearchSelectBottleneckType', {
                        s: this.props.tidewaysContext.service,
                        env: this.props.tidewaysContext.environment,
                    })}
                    navigator={this.getNavigator()}
                    onCriteriaModified={this.onCriteriaModified}
                    openRight
                />
            ),
            transactionTag: (
                <Selector type="tags" removable onRemove={e => this.hideFilter('transactionTag')} headText={criteria.transactionTag || '-'}>
                    <ul className="dropdown-menu" role="menu">
                        {['-', 'cached', 'uncached', 'crawler', 'not crawler'].map(tag => (
                            <li key={tag}>
                                <a onClick={() => this.setQueryParameter({ transactionTag: tag === '-' ? undefined : tag })}>{tag}</a>
                            </li>
                        ))}
                    </ul>
                </Selector>
            ),
            method: (
                <Selector type="method" removable onRemove={e => this.hideFilter('method')} headText={criteria.method || 'All'}>
                    <ul className="dropdown-menu" role="menu">
                        <li>
                            <a onClick={() => this.setQueryParameter({ method: null })}>All</a>
                            <a onClick={() => this.setQueryParameter({ method: 'GET' })}>GET</a>
                            <a onClick={() => this.setQueryParameter({ method: 'POST' })}>POST</a>
                            <a onClick={() => this.setQueryParameter({ method: 'PUT' })}>PUT</a>
                            <a onClick={() => this.setQueryParameter({ method: 'DELETE' })}>DELETE</a>
                            <a onClick={() => this.setQueryParameter({ method: 'PATCH' })}>PATCH</a>
                            <a onClick={() => this.setQueryParameter({ method: 'HEAD' })}>HEAD</a>
                        </li>
                    </ul>
                </Selector>
            ),
            host: (
                <TextInput
                    name={'host'}
                    label={'HTTP Host'}
                    initialValue={criteria.host || ''}
                    removable
                    onRemove={() => {
                        this.hideFilter('host')
                    }}
                    onChange={value => {
                        this.setQueryParameter({ host: value })
                    }}
                />
            ),
            sqlCt: (
                <RangeSelector
                    type="SQL Queries"
                    min={
                        criteria.layers && criteria.layers.db && criteria.layers.db.minCt && typeof criteria.layers.db.minCt !== 'undefined'
                            ? parseInt(criteria.layers.db.minCt, 10)
                            : null
                    }
                    max={
                        criteria.layers && criteria.layers.db && criteria.layers.db.maxCt && typeof criteria.layers.db.maxCt !== 'undefined'
                            ? parseInt(criteria.layers.db.maxCt, 10)
                            : null
                    }
                    minKey="minCt"
                    maxKey="maxCt"
                    onClick={values => this.setLayersQueryParameter(values, 'db')}
                    ranges={[
                        {
                            min: 0,
                            max: 0,
                        },
                        { max: 10 },
                        { max: 25 },
                        { max: 100 },
                        { min: 1 },
                        { min: 10 },
                        { min: 25 },
                        { min: 100 },
                        { min: 500 },
                    ]}
                    removable
                    onRemove={e => this.hideFilter('sqlCt')}
                    showCustom
                    unit="ct"
                />
            ),
            sqlWt: (
                <RangeSelector
                    type="SQL Duration"
                    min={
                        criteria.layers && criteria.layers.db && criteria.layers.db.minWt && typeof criteria.layers.db.minWt !== 'undefined'
                            ? parseInt(criteria.layers.db.minWt, 10)
                            : null
                    }
                    max={
                        criteria.layers && criteria.layers.db && criteria.layers.db.maxWt && typeof criteria.layers.db.maxWt !== 'undefined'
                            ? parseInt(criteria.layers.db.maxWt, 10)
                            : null
                    }
                    minKey="minWt"
                    maxKey="maxWt"
                    onClick={values => this.setLayersQueryParameter(values, 'db')}
                    ranges={[{ max: 10 }, { max: 25 }, { max: 100 }, { min: 10 }, { min: 25 }, { min: 100 }, { min: 500 }, { min: 1000 }]}
                    unit="ms"
                    removable
                    onRemove={e => this.hideFilter('sqlWt')}
                    showCustom
                />
            ),
            httpCt: (
                <RangeSelector
                    type="HTTP Requests"
                    min={
                        criteria.layers &&
                        criteria.layers.http &&
                        criteria.layers.http.minCt &&
                        typeof criteria.layers.http.minCt !== 'undefined'
                            ? parseInt(criteria.layers.http.minCt, 10)
                            : null
                    }
                    max={
                        criteria.layers &&
                        criteria.layers.http &&
                        criteria.layers.http.maxCt &&
                        typeof criteria.layers.http.maxCt !== 'undefined'
                            ? parseInt(criteria.layers.http.maxCt, 10)
                            : null
                    }
                    minKey="minCt"
                    maxKey="maxCt"
                    onClick={values => this.setLayersQueryParameter(values, 'http')}
                    ranges={[
                        {
                            min: 0,
                            max: 0,
                        },
                        { max: 10 },
                        { max: 25 },
                        { max: 100 },
                        { min: 1 },
                        { min: 10 },
                        { min: 25 },
                        { min: 100 },
                        { min: 500 },
                    ]}
                    removable
                    onRemove={e => this.hideFilter('httpCt')}
                    showCustom
                    unit="ct"
                />
            ),
            httpWt: (
                <RangeSelector
                    type="HTTP Duration"
                    min={
                        criteria.layers &&
                        criteria.layers.http &&
                        criteria.layers.http.minWt &&
                        typeof criteria.layers.http.minWt !== 'undefined'
                            ? parseInt(criteria.layers.http.minWt, 10)
                            : null
                    }
                    max={
                        criteria.layers &&
                        criteria.layers.http &&
                        criteria.layers.http.maxWt &&
                        typeof criteria.layers.http.maxWt !== 'undefined'
                            ? parseInt(criteria.layers.http.maxWt, 10)
                            : null
                    }
                    minKey="minWt"
                    maxKey="maxWt"
                    onClick={values => this.setLayersQueryParameter(values, 'http')}
                    ranges={[{ max: 10 }, { max: 25 }, { max: 100 }, { min: 10 }, { min: 25 }, { min: 100 }, { min: 500 }, { min: 1000 }]}
                    unit="ms"
                    removable
                    onRemove={e => this.hideFilter('httpWt')}
                    showCustom
                />
            ),
            httpStatus: (
                <RangeSelector
                    type="HTTP Status"
                    min={'minStatus' in criteria && typeof criteria.minStatus !== 'undefined' ? parseInt(criteria.minStatus, 10) : null}
                    max={'maxStatus' in criteria && typeof criteria.maxStatus !== 'undefined' ? parseInt(criteria.maxStatus, 10) : null}
                    minKey="minStatus"
                    maxKey="maxStatus"
                    onClick={this.setQueryParameter}
                    ranges={[
                        { min: 200, max: 200, label: '200 Success' },
                        { min: 301, max: 301, label: '301 Permanent Redirect' },
                        { min: 302, max: 302, label: '302 Temporary Redirect' },
                        { min: 400, max: 400, label: '400 Bad Request' },
                        { min: 401, max: 401, label: '401 Unauthenticated' },
                        { min: 403, max: 403, label: '403 Unauthorized' },
                        { min: 404, max: 404, label: '404 Not Found' },
                        { min: 500, max: 500, label: '500 Server Error' },
                        { min: 200, max: 299, label: 'All 200s' },
                        { min: 300, max: 399, label: 'All 300s' },
                        { min: 400, max: 499, label: 'All 400s' },
                        { min: 500, max: 599, label: 'All 500s' },
                    ]}
                    removable
                    onRemove={e => this.hideFilter('httpStatus')}
                />
            ),
            search: (
                <div className="form-group">
                    <input
                        placeholder="Search..."
                        type="text"
                        onChange={ev => this.filterSearch(ev.target.value, 800)}
                        value={this.state.searchValue}
                        className="form-control"
                    />
                </div>
            ),
            sort: (
                <div className="form-group" style={{ marginTop: '2px' }}>
                    <div className="btn-group">
                        <div className="btn-group">
                            <button
                                type="button"
                                className="btn btn-default dropdown-toggle navbar-btn"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                {criteria && criteria.sortBy && sortOptions[criteria.sortBy]}
                            </button>
                            <ul className="dropdown-menu">
                                {Object.entries(sortOptions).map(([sortKey, label]) => (
                                    <li key={sortKey}>
                                        <a
                                            onClick={() => {
                                                this.setQueryParameter({ sortBy: sortKey, sortOrder: 'desc' })
                                            }}
                                        >
                                            {label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <button
                            type="button"
                            className="btn btn-default navbar-btn"
                            onClick={() => {
                                this.setQueryParameter({
                                    sortOrder: criteria && criteria.sortOrder === 'asc' ? 'desc' : 'asc',
                                })
                            }}
                        >
                            {criteria && criteria.sortOrder === 'asc' ? (
                                <i className="fa fa-sort-amount-asc" />
                            ) : (
                                <i className="fa fa-sort-amount-desc" />
                            )}
                        </button>
                    </div>
                </div>
            ),
            clear: (
                <div className="form-group" style={{ paddingTop: '8px' }}>
                    <a
                        onClick={e => {
                            this.clearFilters(e)
                        }}
                    >
                        Clear Filters
                    </a>
                </div>
            ),
        }

        const getDefinition = field => {
            if (!field.includes('::')) {
                return <li key={field}>{definitions[field]}</li>
            }

            const [baseField, argument] = field.split('::')

            return <li key={field}>{definitions[baseField](argument)}</li>
        }

        let elements: JSX.Element[] = []
        let additionalFilters: any[] = []

        elements = Array.from(new Set([...this.state.mainFilters, ...actions])).map(getDefinition)
        additionalFilters.push(getDefinition('more'))

        if (this.state.enabledFilters.length > 0) {
            additionalFilters = [...additionalFilters, this.state.enabledFilters.map(getDefinition)]
        }

        let profileTable = <Loading height={500} />
        if (this.props.error && this.props.error.advancedParseError) {
            profileTable = (
                <div className="box-body no-padding" style={{ height: 500 }}>
                    <div style={errorBoxStyle as CSSProperties}>
                        <big className="text-center">
                            <h3>
                                <span className="fa fa-exclamation-circle" /> Error Parsing Your Search Query
                            </h3>
                            <p>{this.props.error.advancedParseError}</p>
                        </big>
                    </div>
                </div>
            )
        } else if (this.props.data) {
            let { profiles } = this.props.data

            // when in compare mode, we do not want to show the currently selected trace
            if (this.props.params && this.props.params.profile) {
                profiles = this.props.data.profiles.filter(profile => profile.id !== this.props.params.profile)
            }

            profileTable = (
                <div className="box-body no-padding">
                    {!this.props.tidewaysContext.application.recievedData ? (
                        <div className="alert alert-info">
                            We have not received data for this project yet. Check the{' '}
                            <Link route="XhprofOrganizationBundle.Application.installation">Setup Wizard</Link> to get started.
                        </div>
                    ) : (
                        <ProfileTable profiles={{ profiles }} profileRoute={this.props.profileRoute} />
                    )}
                </div>
            )
        }

        let infoMessageForESLastPage: JSX.Element | null = null
        let pager: JSX.Element | null = null
        if (this.props.data && this.props.data.total) {
            const maxPossiblePages = 333
            const totalPages = Math.min(Math.ceil(this.props.data.total / 30), maxPossiblePages)

            if (criteria && criteria.page === maxPossiblePages) {
                infoMessageForESLastPage = (
                    <div className="box-body">
                        <div style={errorBoxStyle as CSSProperties} className="text-center">
                            <p>
                                There are {this.props.data.total} results, but we can only show you the last page to up to 10.000 results.
                                You can invert the sort order in order to reach those entries.
                            </p>
                        </div>
                    </div>
                )
            }

            pager = (
                <div className="box-footer text-center">
                    <Pager totalPages={totalPages} currentPage={criteria ? criteria.page : 1} navigator={this.getNavigator()} />
                </div>
            )
        }

        return (
            <div className="row">
                <div className="col-md-12">
                    {this.props.showTabs && <TraceNavigation active="list" showTracepoints={tidewaysContext.application.monitoring} />}
                    <div className="box">
                        <div className="box-header">
                            <nav className="navbar navbar-toolbar">
                                {!historyView && <ul className="nav navbar-nav">{elements}</ul>}
                                {!historyView && additionalFilters.length > 0 && (
                                    <ul style={{ clear: 'left' }} className="nav navbar-nav">
                                        {additionalFilters}
                                    </ul>
                                )}
                                {this.props.hasArchivedTraces ? (
                                    <div className={'pull-right'} style={{ paddingRight: '10px' }}>
                                        {historyView ? (
                                            <a
                                                onClick={() => {
                                                    this.setQueryParameter({ source: 'monitoring' })
                                                }}
                                            >
                                                Compare with current traces
                                            </a>
                                        ) : (
                                            <a
                                                onClick={() => {
                                                    this.setQueryParameter({ source: 'history' })
                                                }}
                                            >
                                                Compare with traces from the history
                                            </a>
                                        )}
                                    </div>
                                ) : null}
                            </nav>
                        </div>
                        {profileTable}
                        {infoMessageForESLastPage}
                        {pager}
                    </div>
                </div>
            </div>
        )
    }

    private filterSearch = (value, delay) => {
        this.setState({ searchValue: value, preventSearchQueryParameterOverride: true })

        if (value.length < 3 && value.length > 0) {
            return
        }

        if (this.delayedSearchTimeout) {
            clearTimeout(this.delayedSearchTimeout as number)
        }

        this.delayedSearchTimeout = setTimeout(
            param => {
                this.setQueryParameter({ search: param })
            },
            delay,
            value,
        )
    }

    private clearFilters = (event, filters = Object.keys(this.props.parameters)) => {
        // To prevent the app recovering itself again from the old criteria
        if (this.props.tidewaysContext.getLocalStorageKey) {
            const localStorageKey = this.props.tidewaysContext.getLocalStorageKey()
            localStorage.removeItem(localStorageKey)
        }

        const preserveGlobalParameters = ['m', 's', 'ts', 'env', 'date', 'end']
        const remove = Object.keys(this.props.parameters).filter(
            item =>
                filters.includes(item) &&
                !preserveGlobalParameters.includes(item) &&
                (!this.props.fixedCriteria || !this.props.fixedCriteria[item]),
        )
        const removeByKey = {}
        Object.values(remove).forEach((key: string) => {
            removeByKey[key] = undefined
        })

        this.props.navigator.replace(removeByKey, this.props.listRoute)
    }

    private hideFilter = (filter?: string) => {
        if (!filter) {
            return
        }

        const filters = this.state.enabledFilters.filter(eachFilter => eachFilter !== filter)
        const toUnsetKeys = this.state.additionalFilters[filter].queryKeys

        this.setState({ enabledFilters: filters })
        this.clearFilters(null, toUnsetKeys)
    }

    private onCriteriaModified = values => {
        // Array parameters are passed as objects
        // convert them back to arrays
        let newParameters = {}
        Object.keys(this.props.parameters).forEach(key => {
            newParameters[key] =
                newParameters[key] === Object(newParameters[key]) ? Object.values(this.props.parameters[key]) : this.props.parameters[key]
        })

        newParameters = { ...newParameters, ...values }

        this.props.onCriteriaModified({
            ...newParameters,
        })
    }

    private displayCustomAnnotations = (): boolean => {
        return this.props.tidewaysContext?.application?.license.customTraceAnnotations
    }
    private displayCustomSpans = (): boolean => this.props.tidewaysContext.application.license.customSpans
}

export default withRouter(
    connect<StateProps, DispatchProps, OwnProps>(
        (globalState: ApplicationState, props: OwnProps & RouteComponentProps) => {
            const profiles: Entity<TraceList | null> = globalState.profiles.profiles || new Entity()
            const criteria: Entity<ExtendedProfileCriteria | null> = globalState.profiles.criteria || new Entity()

            return {
                ...props,
                tidewaysContext: globalState.tidewaysContext,
                navigator: new Navigator(globalState.tidewaysContext.route),
                parameters: parseQueryString(decodeURI(props.location.search)),
                ...profiles,
                criteria: { ...(criteria.data as ExtendedProfileCriteria) },
                data: profiles.data as TraceList,
            }
        },
        dispatch => {
            return {
                onCriteriaModified: values => dispatch({ type: 'Profile.criteriaModified', data: values }),
            }
        },
    )(ProfileList),
)
